export interface ResolveActionsRequest {
  metadata?: { [key: string]: string[] }
}

export interface Action {
  uid: string
  name: string
  objectName?: string
  label: string
  description?: string
  request: Request
  executionMode: ExecutionMode
  ui: CustomActionUserInterface
  fields?: DataField[]
  limit?: number
  successResultTemplate?: SuccessResultTemplate
  securityConfig?: SecurityConfig
  metadata?: object
  isSystemGenerated: boolean
  createdAt: string
  createdBy: string
  updatedAt?: string
  updatedBy?: string
}

export interface BatchExecutionMode extends ExecutionMode {
  batchSize: number
}

export enum ContentType {
  TEXT_PLAIN = 'TEXT_PLAIN',
  APPLICATION_JSON = 'APPLICATION_JSON',
}

export interface CustomActionUserInterface {
  refreshEnabled: boolean
  runInBackground: boolean
  setup: UserInterface
  success: EndProcessInterface
  partialSuccess: EndProcessInterface
  failure: EndProcessInterface
}

export interface CustomEndProcessInterface extends EndProcessInterface {
  templateName: string
  templateData?: { [key: string]: string }
}

export interface CustomUserInterface extends UserInterface {
  templateName: string
  templateData?: { [key: string]: string }
}

export interface DataField {
  name: string
  isPrimaryKey: boolean
}

export interface EndProcessInterface {
  type: EndProcessInterfaceType
}

export enum EndProcessInterfaceType {
  STANDARD = 'standard',
  CUSTOM = 'custom',
}

export interface ExecutionMode {
  type: ExecutionModeType
}

export enum ExecutionModeSingleFailure {
  CONTINUE_ON_FAILURE = 'CONTINUE_ON_FAILURE',
  CANCEL_ON_FAILURE = 'CANCEL_ON_FAILURE',
  UNKNOWN = 'UNKNOWN',
}

export enum ExecutionModeType {
  SINGLE = 'single',
  BATCH = 'batch',
  BULK = 'bulk',
}

export enum Method {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
  UNKNOWN = 'UNKNOWN',
}

export interface Request {
  method: Method
  url: string
  body?: string
  headers?: { [key: string]: string }
}

export interface SecurityConfig {
  permissions: string[]
  featureFlags: string[]
}

export interface SingleExecutionMode extends ExecutionMode {
  failureMode: ExecutionModeSingleFailure
}

export interface StandardEndProcessInterface extends EndProcessInterface {
  title: string
  message: string
}

export interface StandardUserInterface extends UserInterface {
  title: string
  message: string
  confirmationButton: string
}

export interface SuccessResultTemplate {
  template?: string
  contentType?: ContentType
}

export interface UserInterface {
  type: UserInterfaceType
}

export enum UserInterfaceType {
  STANDARD = 'standard',
  CUSTOM = 'custom',
}

export enum ActionTaskStatus {
  Pending = 'Pending',
  InProgress = 'InProgress',
  Successful = 'Successful',
  PartiallySuccessful = 'PartiallySuccessful',
  Failed = 'Failed',
}

export type ActionTaskAction = {
  id: string
  label: string
}

export type ActionRequestResultOk<T = unknown> = {
  ok: true
  keys: string[]
  statusCode: number
  data: T
}

export type ActionRequestResultError<T = unknown> = {
  ok: false
  keys: string[]
  statusCode?: number
  data?: T
  error?: string
}

export type ActionRequestResult<T = unknown> =
  | ActionRequestResultOk<T>
  | ActionRequestResultError<T>

export const ActionRequestResult = {
  isOk<T>(result: ActionRequestResult<T>): result is ActionRequestResultOk<T> {
    return result.ok
  },

  isError<T>(
    result: ActionRequestResult<T>,
  ): result is ActionRequestResultError<T> {
    return !result.ok
  },
}

export type ActionTaskPending = {
  id: string
  status: ActionTaskStatus.Pending
  done: false
  action: ActionTaskAction
  userId: string
  createdTime: Date
}

export type ActionTaskInProgress = {
  id: string
  status: ActionTaskStatus.InProgress
  done: false
  action: ActionTaskAction
  userId: string
  createdTime: Date
  startedTime: Date
}

export type ActionTaskCompleted<T = unknown> = {
  id: string
  status:
    | ActionTaskStatus.Successful
    | ActionTaskStatus.PartiallySuccessful
    | ActionTaskStatus.Failed
  done: true
  action: ActionTaskAction
  userId: string
  results: ActionRequestResult<T>[]
  createdTime: Date
  startedTime: Date
  finishedTime: Date
}

export type ActionTask<T = unknown> =
  | ActionTaskPending
  | ActionTaskInProgress
  | ActionTaskCompleted<T>

export type ActionTaskInput<
  TValue extends Record<string, unknown> = Record<string, unknown>,
  TArgs extends Record<string, unknown> = Record<string, unknown>,
> = {
  actionId: string
  values: TValue[]
  args?: TArgs
}
